import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import CountUp from 'react-countup';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion,
  Play, Download, RefreshCw, ChevronLeft, ChevronRight,
  Info, CheckCircle2, XCircle, AlertTriangle, Clock, Loader2,
  Bug, Lock, FileWarning, Globe, Users, KeyRound, Database, Search,
  Terminal, Fingerprint, Timer, Zap, HardDrive, Wifi, ShieldOff, Eye, Cookie, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const API = `${window.location.origin}/api`;

const CATEGORY_CONFIG = {
  nosql_injection: { label: 'NoSQL Injection', icon: Database, color: 'text-red-600', bg: 'bg-red-50' },
  xss: { label: 'XSS', icon: Bug, color: 'text-orange-600', bg: 'bg-orange-50' },
  auth_bypass: { label: 'Auth Bypass', icon: KeyRound, color: 'text-purple-600', bg: 'bg-purple-50' },
  command_injection: { label: 'CMD Injection', icon: Terminal, color: 'text-red-700', bg: 'bg-red-50' },
  jwt_manipulation: { label: 'JWT Manipülasyonu', icon: Fingerprint, color: 'text-violet-600', bg: 'bg-violet-50' },
  session_fixation: { label: 'Session Fixation', icon: ShieldOff, color: 'text-rose-600', bg: 'bg-rose-50' },
  idor: { label: 'IDOR', icon: Search, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  brute_force: { label: 'Brute Force', icon: Lock, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  rate_limiting: { label: 'Rate Limiting', icon: Timer, color: 'text-amber-600', bg: 'bg-amber-50' },
  stress_test: { label: 'Stres Testi', icon: Zap, color: 'text-sky-600', bg: 'bg-sky-50' },
  file_security: { label: 'Dosya Güvenliği', icon: FileWarning, color: 'text-pink-600', bg: 'bg-pink-50' },
  file_size_limit: { label: 'Dosya Boyut Limiti', icon: HardDrive, color: 'text-fuchsia-600', bg: 'bg-fuchsia-50' },
  ssrf: { label: 'SSRF', icon: Wifi, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  password_policy: { label: 'Şifre Politikası', icon: Lock, color: 'text-blue-700', bg: 'bg-blue-50' },
  permission_bypass: { label: 'Yetki Bypass', icon: Users, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  method_tampering: { label: 'Method Tampering', icon: ShieldAlert, color: 'text-orange-700', bg: 'bg-orange-50' },
  header_security: { label: 'Header Güvenliği', icon: Globe, color: 'text-teal-600', bg: 'bg-teal-50' },
  data_leakage: { label: 'Veri Sızıntısı', icon: Eye, color: 'text-gray-600', bg: 'bg-gray-50' },
  cookie_security: { label: 'Cookie Güvenliği', icon: Cookie, color: 'text-amber-700', bg: 'bg-amber-50' },
};

const STATUS_CONFIG = {
  pass: { label: 'Başarılı', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  fail: { label: 'Başarısız', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  warning: { label: 'Uyarı', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
};

export default function SecurityScan({ user }) {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchScans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/security/scans?page=${page}&limit=20`);
      setScans(res.data.scans);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch {
      toast.error('Tarama geçmişi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page]);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/security/scans/latest`);
      if (res.data) {
        setSelectedScan(res.data);
      }
    } catch { /* no latest scan */ }
  }, []);

  useEffect(() => { fetchScans(); }, [fetchScans]);
  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  useEffect(() => {
    if (!scanning) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/security/scans?page=1&limit=1`);
        const latest = res.data.scans[0];
        if (latest && latest.status === 'completed') {
          setScanning(false);
          const detail = await axios.get(`${API}/security/scans/${latest.id}`);
          setSelectedScan(detail.data);
          fetchScans();
          toast.success('Güvenlik testi tamamlandı!');
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [scanning, fetchScans]);

  const startScan = async () => {
    try {
      setScanning(true);
      await axios.post(`${API}/security/scan`);
      toast.info('Güvenlik testi başlatıldı...');
    } catch (err) {
      setScanning(false);
      toast.error(err.response?.data?.detail || 'Test başlatılamadı');
    }
  };

  const viewScan = async (scanId) => {
    try {
      const res = await axios.get(`${API}/security/scans/${scanId}`);
      setSelectedScan(res.data);
    } catch {
      toast.error('Tarama detayı yüklenemedi');
    }
  };

  const downloadPdf = async (scanId) => {
    try {
      const res = await axios.get(`${API}/security/scans/${scanId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `onxflow_guvenlik_raporu_${scanId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF raporu indirildi');
    } catch {
      toast.error('PDF indirilemedi');
    }
  };

  const deleteScan = async (scanId) => {
    if (!window.confirm('Bu tarama kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/security/scans/${scanId}`);
      toast.success('Tarama kaydı silindi');
      if (selectedScan?.id === scanId) setSelectedScan(null);
      fetchScans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tarama silinemedi');
    }
  };

  const summary = selectedScan?.summary;
  const results = selectedScan?.results || [];

  const filteredResults = results.filter(r => {
    if (filterCategory !== 'all' && r.category !== filterCategory) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  const categoryChartData = results.length > 0
    ? Object.entries(
        results.reduce((acc, r) => {
          const cat = CATEGORY_CONFIG[r.category]?.label || r.category;
          if (!acc[cat]) acc[cat] = { pass: 0, fail: 0, warning: 0 };
          acc[cat][r.status] = (acc[cat][r.status] || 0) + 1;
          return acc;
        }, {})
      ).map(([name, data]) => ({ name, ...data }))
    : [];

  const pieData = summary
    ? [
        { name: 'Başarılı', value: summary.passed, color: '#22c55e' },
        { name: 'Başarısız', value: summary.failed, color: '#ef4444' },
        { name: 'Uyarı', value: summary.warnings, color: '#f59e0b' },
      ].filter(d => d.value > 0)
    : [];

  const trendData = scans
    .filter(s => s.status === 'completed' && s.summary)
    .slice(0, 10)
    .reverse()
    .map(s => ({
      date: s.completedAt ? s.completedAt.split('T')[0].split('-').slice(1).join('/') : '',
      score: s.summary?.score || 0,
      tests: s.summary?.total || 0,
    }));

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!user || user.role !== 'admin') return null;

  if (loading && scans.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Güvenlik testi yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="security-scan-page" className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            G&#252;venlik Testi
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">Otomatik g&#252;venlik testleri ile uygulaman&#305;z&#305; koruyun</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="security-refresh-btn"
            variant="outline"
            size="sm"
            onClick={() => { fetchScans(); fetchLatest(); }}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Yenile</span>
          </Button>
          <Button
            data-testid="security-start-scan-btn"
            onClick={startScan}
            disabled={scanning}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg"
          >
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {scanning ? 'Taranıyor...' : 'Test Başlat'}
          </Button>
        </div>
      </div>

      {/* Scanning Progress */}
      {scanning && (
        <div data-testid="security-scanning-indicator" className="glass rounded-2xl p-6 border-2 border-blue-300 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">G&#252;venlik Testi Devam Ediyor</h3>
              <p className="text-sm text-gray-600 mt-1">NoSQL Injection, XSS, Auth Bypass, IDOR, Brute Force ve di&#287;er testler &#231;al&#305;&#351;&#305;yor...</p>
              <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full animate-[pulse_2s_ease-in-out_infinite]" style={{ width: '60%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-lg flex items-center gap-2 sm:gap-3">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm text-blue-900 font-medium">
            Bu sayfa NoSQL Injection, XSS, Yetki Atlama, Brute Force ve di&#287;er g&#252;venlik testlerini otomatik olarak &#231;al&#305;&#351;t&#305;r&#305;r.
          </p>
          <p className="text-xs text-blue-700 mt-0.5 sm:mt-1 hidden sm:block">
            Sadece admin kullan&#305;c&#305;lar test ba&#351;latabilir ve sonu&#231;lar&#305; g&#246;r&#252;nt&#252;leyebilir.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Son Test Sonucu</h2>
            <span className="text-xs text-gray-500 ml-2">{formatDate(selectedScan?.completedAt)}</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-blue-200" style={{ backgroundColor: '#3b82f615' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 rounded-full bg-blue-500" />
                <span className="font-bold text-sm sm:text-base text-gray-900">G&#252;venlik Skoru</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-gray-900">
                <CountUp end={summary.score} duration={1.5} suffix="%" />
              </div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-indigo-200" style={{ backgroundColor: '#6366f115' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 rounded-full bg-indigo-500" />
                <span className="font-bold text-sm sm:text-base text-gray-900">Toplam Test</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-gray-900"><CountUp end={summary.total} duration={1.5} /></div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-green-200" style={{ backgroundColor: '#22c55e15' }}>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="font-bold text-sm sm:text-base text-gray-900">Ba&#351;ar&#305;l&#305;</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-green-600"><CountUp end={summary.passed} duration={1.5} /></div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-red-200" style={{ backgroundColor: '#ef444415' }}>
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="font-bold text-sm sm:text-base text-gray-900">Ba&#351;ar&#305;s&#305;z</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-red-600"><CountUp end={summary.failed} duration={1.5} /></div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl border-2 border-amber-200" style={{ backgroundColor: '#f59e0b15' }}>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-sm sm:text-base text-gray-900">Uyar&#305;</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-amber-600"><CountUp end={summary.warnings} duration={1.5} /></div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {trendData.length > 1 && (
            <div className="glass rounded-2xl p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                G&#252;venlik Skoru Trendi
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                  <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                  <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', r: 5 }} name="Skor %" animationDuration={1500} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ShieldQuestion className="w-5 h-5 text-blue-600" />
              Sonu&#231; Da&#287;&#305;l&#305;m&#305;
            </h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" labelLine={true} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={90} dataKey="value" animationDuration={1500} animationBegin={200}>
                    {pieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
                <ShieldQuestion className="w-16 h-16 mb-3 opacity-30" />
                <p className="text-sm">Veri yok</p>
              </div>
            )}
          </div>
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-blue-600" />
              Kategori Bazl&#305; Sonu&#231;lar
            </h3>
            {categoryChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={categoryChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="pass" stackId="a" fill="#22c55e" name="Başarılı" animationDuration={1500} />
                  <Bar dataKey="fail" stackId="a" fill="#ef4444" name="Başarısız" animationDuration={1500} />
                  <Bar dataKey="warning" stackId="a" fill="#f59e0b" name="Uyarı" animationDuration={1500} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
                <ShieldAlert className="w-16 h-16 mb-3 opacity-30" />
                <p className="text-sm">Veri yok</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detailed Results Table */}
      {results.length > 0 && (
        <div className="glass rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Detayl&#305; Test Sonu&#231;lar&#305;</h2>
              <span className="ml-2 min-w-[1.5rem] h-6 px-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold rounded-md inline-flex items-center justify-center shadow-lg">
                {filteredResults.length}
              </span>
            </div>
            {selectedScan?.id && (
              <Button data-testid="security-download-pdf-btn" variant="outline" size="sm" onClick={() => downloadPdf(selectedScan.id)} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                PDF &#304;ndir
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <Label>Kategori</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="security-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">T&#252;m Kategoriler</SelectItem>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="security-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">T&#252;m Durumlar</SelectItem>
                  <SelectItem value="pass">Ba&#351;ar&#305;l&#305;</SelectItem>
                  <SelectItem value="fail">Ba&#351;ar&#305;s&#305;z</SelectItem>
                  <SelectItem value="warning">Uyar&#305;</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategori</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detay</th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">&#214;nem</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredResults.map((r, index) => {
                  const statusCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.warning;
                  const catCfg = CATEGORY_CONFIG[r.category] || { label: r.category, icon: ShieldQuestion, color: 'text-gray-600', bg: 'bg-gray-50' };
                  const StatusIcon = statusCfg.icon;
                  const CatIcon = catCfg.icon;
                  return (
                    <tr key={r.id} data-testid={`security-result-row-${r.id}`} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${statusCfg.bg} ${statusCfg.color}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {statusCfg.label}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium ${catCfg.bg} ${catCfg.color}`}>
                          <CatIcon className="w-3 h-3" />
                          {catCfg.label}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-3"><span className="text-sm font-medium text-gray-900">{r.testName}</span></td>
                      <td className="px-3 sm:px-4 py-3"><span className="text-sm text-gray-700 max-w-xs block truncate" title={r.detail}>{r.detail}</span></td>
                      <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold uppercase ${r.severity === 'critical' ? 'bg-red-100 text-red-700' : r.severity === 'high' ? 'bg-orange-100 text-orange-700' : r.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                          {r.severity}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scan History */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Test Ge&#231;mi&#351;i</h2>
            <span className="ml-2 min-w-[1.5rem] h-6 px-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold rounded-md inline-flex items-center justify-center shadow-lg">{total}</span>
          </div>
        </div>
        {scans.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Hen&#252;z Test Yap&#305;lmam&#305;&#351;</h3>
            <p className="text-gray-500">&#304;lk g&#252;venlik testinizi ba&#351;latmak i&#231;in yukar&#305;daki butona t&#305;klay&#305;n.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skor</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Testler</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">S&#252;re</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">&#304;&#351;lemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {scans.map((scan, index) => (
                    <tr key={scan.id} data-testid={`security-scan-row-${scan.id}`} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(scan.createdAt)}</div>
                        <div className="text-xs text-gray-400">{scan.startedByName}</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        {scan.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Tamamland&#305;</span>
                        ) : scan.status === 'running' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700"><Loader2 className="w-3.5 h-3.5 animate-spin" /> &#199;al&#305;&#351;&#305;yor</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-gray-50 text-gray-600"><Clock className="w-3.5 h-3.5" /> Bekliyor</span>
                        )}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap">
                        {scan.summary ? (
                          <span className={`text-lg font-bold ${scan.summary.score >= 80 ? 'text-green-600' : scan.summary.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{scan.summary.score}%</span>
                        ) : '-'}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap">
                        {scan.summary ? (
                          <div className="text-sm"><span className="text-green-600">{scan.summary.passed}</span>{' / '}<span className="text-red-600">{scan.summary.failed}</span>{' / '}<span className="text-amber-600">{scan.summary.warnings}</span></div>
                        ) : '-'}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-500">{scan.duration ? `${scan.duration}s` : '-'}</td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {scan.status === 'completed' && (
                            <>
                              <Button data-testid={`security-view-scan-${scan.id}`} variant="outline" size="sm" onClick={() => viewScan(scan.id)} className="text-xs">Detay</Button>
                              <Button data-testid={`security-pdf-scan-${scan.id}`} variant="outline" size="sm" onClick={() => downloadPdf(scan.id)} className="text-xs"><Download className="w-3 h-3" /></Button>
                              {user?.role === 'admin' && (
                                <Button data-testid={`security-delete-scan-${scan.id}`} variant="outline" size="sm" onClick={() => deleteScan(scan.id)} className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"><Trash2 className="w-3 h-3" /></Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">Toplam {total} test</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><ChevronLeft className="w-4 h-4" /></Button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pageNum = page <= 3 ? i + 1 : page - 2 + i;
                    if (pageNum > totalPages || pageNum < 1) return null;
                    return (<Button key={pageNum} variant={pageNum === page ? 'default' : 'outline'} size="sm" onClick={() => setPage(pageNum)} className="min-w-[2rem]">{pageNum}</Button>);
                  })}
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
